import React, { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import last from 'lodash/last'
import { ConfirmModal, FormInput } from '@microservices/wiskey-react-components'
import { Add } from '@mui/icons-material'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import { Box, Button, IconButton } from '@mui/material'

import { FIELD_VALUE_TYPE, FORMATS_DATE_OPTIONS, GENERATOR_INPUT_TYPE } from '@constants'
import { AutocompleteOption } from '@types'

import { ConfigField } from '@gantt/components/GanttCreateOrEdit'
import { OptionsFilter } from '@gantt/types'

import { PickerItem } from './PickerItem'

type Props = {
  name: string
  disabled: boolean
  isFlatOptions: boolean
  object?: AutocompleteOption<string> | null
  label: string
  prefix?: string
  optionsFilter?: OptionsFilter
  existingPaths?: string[]
  isShowFormatDateField?: boolean
}

export const PickerFieldArrayController = ({
  name,
  disabled = false,
  isFlatOptions,
  object,
  label,
  prefix,
  optionsFilter,
  existingPaths,
  isShowFormatDateField,
}: Props) => {
  const { watch, setValue } = useFormContext()

  const watchedFields: ConfigField[] = watch(name)

  const { t } = useTranslation()
  const [isShowClearModal, setIsShowClearModal] = useState(false)
  const [selectedPaths, setSelectedPaths] = useState<string[]>([])
  const currentFieldsList: ConfigField[] = watch(name)

  const handleAppend = () => {
    setValue(name, [...watchedFields, { pathStr: '', pathArray: [], field: '' }])
  }

  useEffect(() => {
    if (!watchedFields?.length) {
      setValue(name, [{ pathStr: '', pathArray: [], field: '' }])
    }
  }, [])

  const handleRemove = (index: number) => {
    if (watchedFields?.length > 1) {
      setValue(
        name,
        watchedFields?.filter((_, fieldIndex) => fieldIndex !== index)
      )
    }
    if (watchedFields?.length === 1) {
      setValue(`${name}.${index}.formatDate`, null, { shouldDirty: true })

      const paths = currentFieldsList.map(field => field.pathStr)

      handleSetSelectedPaths(paths)
    }
  }

  const handleDeleteAll = () => {
    setValue(name, [{ pathStr: '', pathArray: [], field: '' }], { shouldDirty: true })
    handleCloseClearConfirmModal()
  }

  const handleCloseClearConfirmModal = () => {
    setIsShowClearModal(false)
  }

  const handleOpenConfirmModal = () => {
    setIsShowClearModal(true)
  }

  const handleChangeFieldFormatDate = (name: string, value?: AutocompleteOption) => {
    setValue(name, value?.id || null, { shouldDirty: true })
  }

  const handleSaveField = useCallback(
    (index: number) => () => {
      setValue(`${name}.${index}.formatDate`, null, { shouldDirty: true })
    },
    [name, setValue]
  )

  const handleSetSelectedPaths = useCallback((paths: string[]) => {
    setSelectedPaths(paths)
  }, [])

  useEffect(() => {
    if (currentFieldsList?.length) {
      const paths = currentFieldsList.map(field => field.pathStr)
      handleSetSelectedPaths(paths)
    }
  }, [currentFieldsList])

  return (
    <>
      {watchedFields?.map((field, index) => {
        const valueType = last((field as ConfigField)?.pathArray)?.valueType || null
        const formatDate = field.formatDate

        return (
          <>
            <PickerItem
              key={field.id}
              currentFieldsList={currentFieldsList}
              embeddedObjectPickerControllerName={`${name}.${index}.pathArray`}
              existingPaths={existingPaths}
              field={field}
              fieldName={`${name}.${index}.field`}
              index={index}
              isDisabled={disabled}
              isFlatOptions={isFlatOptions}
              isSingleField={watchedFields?.length === 1}
              label={label}
              name={`${name}.${index}.pathStr`}
              object={object}
              optionsFilter={optionsFilter}
              prefix={prefix}
              selectedPaths={selectedPaths}
              watchValue={watch(`${name}.${index}`)}
              onRemove={() => handleRemove(index)}
              onSave={handleSaveField(index)}
              onSetSelectedPaths={handleSetSelectedPaths}
            />
            {valueType === FIELD_VALUE_TYPE.DATETIME && isShowFormatDateField && (
              <FormInput
                autocompleteOptions={FORMATS_DATE_OPTIONS}
                disableClearable={!formatDate}
                inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                label={t('ganttCreate.columnForm.columnInputs.formatDate.label')}
                name={`${name}.${index}.formatDate`}
                placeholder={t('ganttCreate.columnForm.columnInputs.formatDate.placeholder')}
                value={formatDate ? { id: formatDate, label: formatDate } : null}
                onChangeAutocomplete={formatDate =>
                  handleChangeFieldFormatDate(`${name}.${index}.formatDate`, formatDate)
                }
              />
            )}
          </>
        )
      })}
      <Box display='flex' gap={2} justifyContent='flex-end'>
        <Button onClick={handleAppend}>
          <Add fontSize='small' />
          {t('ganttCreate.common.add')}
        </Button>
        <IconButton size='small' sx={{ width: 35 }} onClick={handleOpenConfirmModal}>
          <DeleteSweepIcon />
        </IconButton>
      </Box>
      {isShowClearModal && (
        <ConfirmModal
          actionBtnText={t('modal.clearFieldArray.btn')}
          containerStyle={{ px: 2.5, py: 2.5, borderRadius: 0 }}
          isShow={isShowClearModal}
          text={t('modal.clearFieldArray.warning')}
          title={t('modal.clearFieldArray.title')}
          onClose={handleCloseClearConfirmModal}
          onConfirm={handleDeleteAll}
        />
      )}
    </>
  )
}
